<template>
  <section class="oa-section">
    <!-- Saving Overlay -->
    <CommonOverlay
      :show="saving"
      title="Saving Settings"
      message="Please wait while we save your settings..."
    />

    <div class="oa-section-header">
      <h2><i class="fa fa-sliders"></i> General Settings</h2>
      <button class="oa-btn oa-btn-save oa-header-save-btn" @click="save" :disabled="saving">
        <i class="fa fa-save"></i>
        Save General Settings
      </button>
    </div>

    <!-- Location Selector (only if multiple paid locations) -->
    <LocationSelector
      v-if="hasMultipleLocations"
      v-model="selectedSiteId"
      :locations="paidLocations"
      :locationStatus="locationStatus"
      label="Configuring for:"
      :showCopyButton="true"
      @copy-settings="showCopyModal"
    />

    <!-- Section: Online Scheduling Status -->
    <div class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-power-off"></i>
        Online Scheduling Status
      </h3>
      <div class="oa-radio-group">
        <label class="oa-radio-option">
          <input type="radio" :value="true" v-model="local.onlineSchedulingEnabled" />
          <span>Online Scheduling is <strong>ON</strong></span>
        </label>
        <label class="oa-radio-option">
          <input type="radio" :value="false" v-model="local.onlineSchedulingEnabled" />
          <span>Online Scheduling is <strong>OFF</strong></span>
        </label>
      </div>
      <p v-if="!local.onlineSchedulingEnabled" class="oa-form-help" style="margin-top: 8px; color: #b45309;">
        <i class="fa fa-exclamation-triangle"></i>
        Patients visiting your booking link will see a message to call your office instead.
      </p>
    </div>

    <!-- Section: Scheduling Window (only when ON) -->
    <div v-if="local.onlineSchedulingEnabled" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-calendar"></i>
        Scheduling Window
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Control how soon, and how far into the future, patients can book appointments.
      </p>
      <div class="oa-form-group">
        <label class="oa-form-label">Start Date</label>
        <div style="display: flex; align-items: center; gap: 8px;">
          <input type="date" class="oa-form-input" style="max-width: 220px;" v-model="local.schedulingWindowStart" />
          <button v-if="local.schedulingWindowStart" class="oa-btn" style="padding: 4px 10px; font-size: 13px;" @click="local.schedulingWindowStart = null">&#10005; Clear</button>
        </div>
        <p class="oa-form-help">Earliest date patients can book. If blank, patients can book starting today.</p>
      </div>
      <div class="oa-form-group">
        <label class="oa-form-label">End Date</label>
        <div style="display: flex; align-items: center; gap: 8px;">
          <input type="date" class="oa-form-input" style="max-width: 220px;" v-model="local.schedulingWindowEnd" :disabled="local.schedulingWindowAutoExtend" />
          <button v-if="local.schedulingWindowEnd && !local.schedulingWindowAutoExtend" class="oa-btn" style="padding: 4px 10px; font-size: 13px;" @click="local.schedulingWindowEnd = null">&#10005; Clear</button>
        </div>
        <p v-if="!local.schedulingWindowAutoExtend" class="oa-form-help">Latest date patients can browse to. If blank, no future limit.</p>
        <p v-else class="oa-form-help">Patients can currently book through <strong>{{ effectiveEndLabel }}</strong> (office time); this moves forward every day.</p>
      </div>

      <div class="oa-checkbox-item">
        <input type="checkbox" id="schedulingWindowAutoExtend" v-model="local.schedulingWindowAutoExtend" />
        <label for="schedulingWindowAutoExtend">
          <span>Automatically extend the booking window</span>
          <p class="oa-checkbox-help">Patients can always book up to the number of months you choose, counted from today. You never have to move the End Date again.</p>
        </label>
      </div>

      <div v-if="local.schedulingWindowAutoExtend" class="oa-block-dropdown">
        <label class="oa-form-label" style="font-size: 0.75rem;">How far ahead can patients book?</label>
        <select class="oa-form-input" style="max-width: 220px;" v-model="local.schedulingWindowAutoExtendMonths">
          <option v-for="n in 6" :key="n" :value="n">{{ n }} month{{ n === 1 ? '' : 's' }} ahead</option>
        </select>
      </div>

      <p v-if="schedulingWindowStartWarning" class="oa-form-help" style="margin-top: 8px; color: #b45309;">
        <i class="fa fa-exclamation-triangle"></i>
        Your Start Date is after the rolling End Date — nothing is bookable until the window reaches it.
      </p>

      <p v-if="schedulingWindowNudgeText" class="oa-form-help" style="margin-top: 8px;">
        <i class="fa fa-lightbulb-o"></i>
        {{ schedulingWindowNudgeText }}
      </p>

      <!-- Minimum notice: stored as ONE integer, minimumNoticeHours (Days = x 24). Blank = none. -->
      <div class="oa-form-group" data-test="minimum-notice" style="margin-top: 16px;">
        <label class="oa-form-label" for="minimumNoticeValue">Minimum Notice</label>
        <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
          <input
            id="minimumNoticeValue"
            data-test="minimum-notice-value"
            type="number"
            min="0"
            step="1"
            inputmode="numeric"
            placeholder="None"
            class="oa-form-input"
            style="max-width: 120px;"
            :max="noticeInput.unit === 'days' ? minimumNoticeMaxDays : minimumNoticeMaxHours"
            v-model="noticeInput.value"
          />
          <select
            data-test="minimum-notice-unit"
            class="oa-form-input"
            style="max-width: 120px;"
            aria-label="Minimum notice unit"
            v-model="noticeInput.unit"
          >
            <option value="hours">Hours</option>
            <option value="days">Days</option>
          </select>
          <button
            v-if="noticeInput.value !== '' && noticeInput.value !== null"
            class="oa-btn"
            style="padding: 4px 10px; font-size: 13px;"
            @click="noticeInput = { value: '', unit: 'hours' }"
          >&#10005; Clear</button>
        </div>
        <p v-if="minimumNoticeHelp" class="oa-form-help">{{ minimumNoticeHelp }}</p>
        <p v-else class="oa-form-help" style="color: #b45309;">
          <i class="fa fa-exclamation-triangle"></i>
          {{ invalidMinimumNoticeMessage }}
        </p>
      </div>
    </div>

    <!-- Section 1: Practice Info -->
    <div class="oa-form-group">
      <label class="oa-form-label">Practice Name</label>
      <div class="oa-practice-name-display">{{ siteName || 'Not Available' }}</div>
    </div>

    <div class="oa-form-group">
      <label class="oa-form-label">Notification Email</label>
      <input type="email" class="oa-form-input" v-model="local.notificationEmail" placeholder="office@example.com" />
      <div class="oa-checkbox-item" style="margin-top: 8px;">
        <input type="checkbox" id="sendAppointmentNotifications" v-model="local.sendAppointmentNotifications" />
        <label for="sendAppointmentNotifications">Send notification to this email whenever a patient schedules an appointment online</label>
      </div>
    </div>

    <div class="oa-form-group">
      <label class="oa-form-label">Alternative Phone Number for Patients</label>
      <input type="tel" class="oa-form-input" v-model="local.officePhone" placeholder="(555) 123-4567" />
      <p class="oa-form-help">
        Optional. If set, this number is shown to patients on the online booking widget instead of
        your office's main phone number (from Open Dental). Useful for routing scheduling questions
        to a different line or for tracking. Leave blank to use your main office phone.
      </p>
    </div>

    <div class="oa-form-group">
      <label class="oa-form-label">Post-Booking Redirect URL</label>
      <input type="url" class="oa-form-input" v-model.trim="local.thankYouUrl" placeholder="https://yourwebsite.com/thank-you" />
      <p class="oa-form-help">After a patient books online, redirect them to this URL instead of the default confirmation screen. Leave blank to use the built-in confirmation. UTM parameters will be forwarded automatically.</p>
    </div>

    <!-- Online Scheduling URL (single-office only) -->
    <div v-if="!hasMultipleLocations" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-link"></i>
        Online Scheduling URL
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Share this link on your website, social media, or in marketing campaigns. Use <strong>Grab a Link</strong> to add UTM campaign tracking parameters — this lets you track which ads, emails, or social posts drive the most bookings.
      </p>
      <div class="oa-location-urls-table">
        <div class="oa-location-url-row">
          <div class="oa-location-url-name">
            <i class="fa fa-calendar-check-o"></i>
            Your Booking Link
          </div>
          <div class="oa-location-url-link">
            <template v-if="singleLinkShortUrl && singleLinkStatus === 'success'">
              <span class="oa-location-url-text">{{ singleLinkShortUrl }}</span>
              <button class="oa-btn oa-btn-link" @click="copySingleLinkUrl" title="Build a link with campaign tracking">
                <i class="fa fa-link"></i> Grab a Link
              </button>
            </template>
            <template v-else-if="singleLinkStatus === 'error'">
              <span class="oa-location-url-text oa-location-url-error">
                <i class="fa fa-exclamation-triangle"></i> Failed to load link
              </span>
              <button class="oa-btn oa-btn-copy" @click="generateSingleLinkShortUrl" title="Retry">
                <i class="fa fa-refresh"></i>
              </button>
            </template>
            <template v-else>
              <span class="oa-location-url-text oa-location-url-loading">
                <i class="fa fa-spinner fa-spin"></i> Generating link...
              </span>
              <button class="oa-btn oa-btn-copy" disabled>
                <i class="fa fa-clipboard"></i>
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Multi-Location Selection -->
    <div v-if="hasMultipleLocations" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-map-marker"></i>
        Location Selection
      </h3>
      <div class="oa-info-box">
        <p>
          <i class="fa fa-info-circle"></i>
          <strong>Multi-Location Setup Detected:</strong> Your practice has multiple locations. Choose how patients access online scheduling.
        </p>
      </div>

      <p class="oa-radio-question">How should patients access online scheduling for your locations?</p>

      <div class="oa-location-mode-cards">
        <!-- Option 1: Single Link — Patients Choose -->
        <label class="oa-mode-card" :class="{ 'oa-mode-card-selected': local.locationMode === 'combined' }">
          <input type="radio" value="combined" v-model="local.locationMode" />
          <div class="oa-mode-card-content">
            <div class="oa-mode-card-title"><i class="fa fa-link"></i> Single Link &mdash; Patients Choose Location</div>
            <p class="oa-mode-card-desc">One URL for all locations. Patients pick their preferred location when they start booking.</p>
          </div>
        </label>

        <!-- Option 2: Single Link — Default Location -->
        <label class="oa-mode-card" :class="{ 'oa-mode-card-selected': local.locationMode === 'default' }">
          <input type="radio" value="default" v-model="local.locationMode" />
          <div class="oa-mode-card-content">
            <div class="oa-mode-card-title"><i class="fa fa-map-pin"></i> Single Link &mdash; Default Location</div>
            <p class="oa-mode-card-desc">One URL that always books at a specific location. No location choice for patients.</p>
          </div>
        </label>

        <!-- Option 3: Separate Links -->
        <label class="oa-mode-card" :class="{ 'oa-mode-card-selected': local.locationMode === 'separate' }">
          <input type="radio" value="separate" v-model="local.locationMode" />
          <div class="oa-mode-card-content">
            <div class="oa-mode-card-title"><i class="fa fa-external-link"></i> Separate Links &mdash; One Per Location</div>
            <p class="oa-mode-card-desc">Each location gets its own booking URL with its own branding. Use this if your locations have separate websites.</p>
          </div>
        </label>
      </div>

      <!-- Combined mode: enabled locations checkboxes -->
      <div v-if="local.locationMode === 'combined'" class="oa-location-list">
        <p class="oa-form-help">Select which locations patients can choose from:</p>
        <div v-for="loc in paidLocations" :key="loc.id || loc.siteId" class="oa-checkbox-item">
          <input type="checkbox" :id="'loc-' + (loc.id || loc.siteId)" :checked="isLocationEnabled(loc.id || loc.siteId)" @change="toggleLocation(loc)" />
          <label :for="'loc-' + (loc.id || loc.siteId)">{{ loc.name }}<span v-if="loc.city">, {{ loc.city }}</span><span v-if="loc.state">, {{ loc.state }}</span></label>
        </div>
      </div>

      <!-- Default mode: default location dropdown -->
      <div v-if="local.locationMode === 'default'" class="oa-location-list">
        <p class="oa-form-help">Select default location:</p>
        <select class="oa-form-input" v-model="local.defaultLocationId">
          <option :value="null">Select a default location...</option>
          <option v-for="loc in paidLocations" :key="loc.id || loc.siteId" :value="loc.id || loc.siteId">
            {{ loc.name }}<template v-if="loc.city">, {{ loc.city }}</template><template v-if="loc.state">, {{ loc.state }}</template>
          </option>
        </select>
      </div>

      <!-- Single-link URL display (for 'combined' and 'default' modes) -->
      <div v-if="local.locationMode === 'combined' || (local.locationMode === 'default' && local.defaultLocationId)" class="oa-location-list" style="margin-top: 16px;">
        <p class="oa-form-help" style="margin-bottom: 8px;">
          <strong>Your booking link:</strong> Use <strong>Grab a Link</strong> to add UTM campaign tracking — track which ads, emails, or social posts drive the most bookings.
        </p>
        <div class="oa-location-urls-table">
          <div class="oa-location-url-row">
            <div class="oa-location-url-name">
              <i class="fa fa-link"></i>
              {{ local.locationMode === 'combined' ? 'All Locations' : getDefaultLocationName() }}
            </div>
            <div class="oa-location-url-link">
              <template v-if="singleLinkShortUrl && singleLinkStatus === 'success'">
                <span class="oa-location-url-text">{{ singleLinkShortUrl }}</span>
                <button class="oa-btn oa-btn-link" @click="copySingleLinkUrl" title="Build a link with campaign tracking">
                  <i class="fa fa-link"></i> Grab a Link
                </button>
              </template>
              <template v-else-if="singleLinkStatus === 'error'">
                <span class="oa-location-url-text oa-location-url-error">
                  <i class="fa fa-exclamation-triangle"></i> Failed to load link
                </span>
                <button class="oa-btn oa-btn-copy" @click="generateSingleLinkShortUrl" title="Retry">
                  <i class="fa fa-refresh"></i>
                </button>
              </template>
              <template v-else>
                <span class="oa-location-url-text oa-location-url-loading">
                  <i class="fa fa-spinner fa-spin"></i> Generating link...
                </span>
                <button class="oa-btn oa-btn-copy" disabled>
                  <i class="fa fa-clipboard"></i>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Separate mode: per-location URL table -->
      <div v-if="local.locationMode === 'separate'" class="oa-location-list">
        <p class="oa-form-help" style="margin-bottom: 8px;">
          Each location has its own booking link. Use <strong>Grab a Link</strong> to add UTM campaign tracking — track which ads, emails, or social posts drive the most bookings per location.
        </p>
        <div class="oa-location-urls-header">
          <button class="oa-btn oa-btn-secondary oa-btn-sm" @click="refreshAllLocationUrls" :disabled="generatingShortUrls">
            <i :class="generatingShortUrls ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
            {{ generatingShortUrls ? 'Generating...' : 'Reload Links' }}
          </button>
        </div>
        <div class="oa-location-urls-table">
          <div v-for="loc in paidLocations" :key="'url-' + (loc.id || loc.siteId)" class="oa-location-url-row">
            <div class="oa-location-url-name">
              <i class="fa fa-map-marker"></i>
              {{ loc.name }}<span v-if="loc.city" class="oa-location-url-city">, {{ loc.city }}</span>
            </div>
            <div class="oa-location-url-link">
              <!-- Success: show URL + grab a link -->
              <template v-if="locationShortUrls[loc.id || loc.siteId]">
                <span class="oa-location-url-text">{{ locationShortUrls[loc.id || loc.siteId] }}</span>
                <button
                  class="oa-btn oa-btn-link"
                  @click="copyLocationUrl(loc.id || loc.siteId)"
                  title="Build a link with campaign tracking"
                >
                  <i class="fa fa-link"></i> Grab a Link
                </button>
              </template>
              <!-- Error: show error + retry -->
              <template v-else-if="locationUrlStatus[loc.id || loc.siteId] === 'error'">
                <span class="oa-location-url-text oa-location-url-error">
                  <i class="fa fa-exclamation-triangle"></i> Failed to load link
                </span>
                <button class="oa-btn oa-btn-copy" @click="retryLocationUrl(loc.id || loc.siteId)" title="Retry">
                  <i class="fa fa-refresh"></i>
                </button>
              </template>
              <!-- Loading: show spinner -->
              <template v-else>
                <span class="oa-location-url-text oa-location-url-loading">
                  <i class="fa fa-spinner fa-spin"></i> Generating link...
                </span>
                <button class="oa-btn oa-btn-copy" disabled>
                  <i class="fa fa-clipboard"></i>
                </button>
              </template>
            </div>
          </div>
        </div>
        <p class="oa-form-help" style="margin-top: 12px;">
          <i class="fa fa-lightbulb-o"></i>
          Each location can have its own branding (logo, colors, background image). Switch locations using the selector above, then configure the <strong>Patient-Facing Branding</strong> section below for each location.
        </p>
      </div>
    </div>

    <!-- Section 3: Insurances Accepted -->
    <div class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-medkit"></i>
        Insurances Accepted
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Select the insurance plans your practice accepts. These will be displayed to patients during online booking.
      </p>
      <multiselect
        v-model="local.acceptedInsurances"
        :options="insuranceOptions"
        :multiple="true"
        placeholder="Select insurance plans..."
        :close-on-select="false"
      />
    </div>

    <!-- Section 4: Provider Network Status -->
    <div v-if="local.acceptedInsurances && local.acceptedInsurances.length > 0" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-users"></i>
        Provider Network Status
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        For each insurance plan, indicate which providers are in-network.
      </p>

      <div class="oa-network-table-wrapper">
        <table class="oa-network-table">
          <thead>
            <tr>
              <th class="oa-network-insurance-col">Insurance</th>
              <th class="oa-network-all-col">All</th>
              <th v-for="provider in providersList" :key="provider" class="oa-network-provider-col">
                {{ provider }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="insurance in local.acceptedInsurances" :key="insurance">
              <td class="oa-network-insurance-col">{{ insurance }}</td>
              <td class="oa-network-all-col">
                <input
                  type="checkbox"
                  :checked="isAllProvidersChecked(insurance)"
                  :indeterminate.prop="isIndeterminate(insurance)"
                  @change="toggleAllProviders(insurance, $event.target.checked)"
                />
              </td>
              <td v-for="provider in providersList" :key="provider" class="oa-network-provider-col">
                <input
                  type="checkbox"
                  :checked="isProviderInNetwork(insurance, provider)"
                  @change="toggleProvider(insurance, provider, $event.target.checked)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="insuranceOptions.length > 0" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-users"></i>
        Provider Network Status
      </h3>
      <p class="oa-form-help">Select insurances above to configure provider network status.</p>
    </div>

    <!-- Section 5: Patient Display Options -->
    <div class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-eye"></i>
        Patient Display Options
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Control what insurance network information patients see during online booking.
      </p>
      <div class="oa-checkbox-item">
        <input type="checkbox" id="showNetworkStatusChart" v-model="local.showNetworkStatusChart" />
        <label for="showNetworkStatusChart">Show Provider in-network / out-of-network status chart to Patients when they schedule online</label>
      </div>
      <div class="oa-checkbox-item">
        <input type="checkbox" id="showNetworkStatusOnProviderSelect" v-model="local.showNetworkStatusOnProviderSelect" />
        <label for="showNetworkStatusOnProviderSelect">Show in-network / out-of-network status when they select a provider</label>
      </div>
    </div>

    <!-- Section 6: No Fee Code for Appointment Insert -->
    <div v-if="procedureCodes && procedureCodes.length > 0" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-code"></i>
        No Fee Code to use when Inserting Appointment into Open Dental
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Which code would you like to use when an appointment is inserted into Open Dental?
      </p>
      <multiselect
        v-model="local.noFeeCodeObj"
        :options="procedureCodes"
        :multiple="false"
        :custom-label="noFeeCodeLabel"
        label="ProcCode"
        track-by="ProcCode"
        placeholder="Search and select a procedure code..."
        :allow-empty="true"
        :show-labels="false"
      />
      <div class="oa-important-box">
        <div class="oa-important-box-header">
          <i class="fa fa-exclamation-triangle"></i>
          <strong>Important:</strong>
        </div>
        <p>Most offices have configured Open Dental (by default) to require at least <strong>ONE</strong> procedure code to be attached to an appointment that's on schedule.</p>
        <p>The code you select should be a <strong>ZERO FEE</strong> code.<br>Examples: PostOp, office consult, etc.</p>
        <p>Under the <strong>Appointment Types</strong> — you will be able to select which codes should populate within the appointment note as they do not get attached. They're only used as reference for your team, so they can attach the proper codes to the appointment.</p>
      </div>
    </div>

    <!-- Section 7: Appointment Confirmation Status -->
    <div v-if="confirmationStatuses && confirmationStatuses.length > 0" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-check-circle"></i>
        Appointment Open Dental Confirmation Status
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Which Appointment Confirmation Status do you want the appointments to have when inserted into Open Dental?
      </p>
      <select class="oa-form-input" v-model="local.appointmentConfirmedStatus">
        <option :value="null">Select a confirmation status...</option>
        <option v-for="cs in confirmationStatuses" :key="cs.DefNum" :value="cs.DefNum">
          {{ cs.ItemName }}
        </option>
      </select>
    </div>

    <!-- Appointment Confirmation Message for Patients -->
    <div class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-comment"></i>
        Appointment Confirmation Message for Patients
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Choose a wiki template to use for the SMS confirmation patients receive after booking online.
        Only wiki pages tagged <code>online-appointments</code> appear here. Leave blank to use the default Teamio message.
        Available variables: <code v-pre>{{patient_first_name}}</code>, <code v-pre>{{apt_date}}</code>,
        <code v-pre>{{online_apttype}}</code>, <code v-pre>{{apt_provider}}</code>,
        <code v-pre>{{site_name}}</code>, <code v-pre>{{site_officephone}}</code>.
      </p>
      <select class="oa-form-input" v-model="local.confirmationWikiPageID">
        <option :value="null">Use default Teamio message</option>
        <option v-for="t in confirmationTemplates" :key="t.WikiPageID" :value="t.WikiPageID">
          {{ t.Name }}
        </option>
      </select>
      <p v-if="!loadingConfirmationTemplates && confirmationTemplates.length === 0" class="oa-form-help" style="margin-top: 8px; color: #6b7280;">
        No templates found. Create a wiki page, tag it <code>online-appointments</code>, then refresh.
      </p>
    </div>

    <!-- Section 8: Blockout Types -->
    <div v-if="blockoutTypes && blockoutTypes.length > 0" class="oa-divider-section">
      <h3 class="oa-subsection-title">
        <i class="fa fa-ban"></i>
        Don't Schedule in these "Blocks"
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Select the block types patients should never be booked into online. Any opening covered by
        these blocks &mdash; Lunch, staff meetings, and similar &mdash; is removed from the booking page.
        Blocks are matched per operatory, so a block only closes the operatories it is drawn on.
      </p>
      <div v-for="bt in blockoutTypes" :key="bt.DefNum" class="oa-checkbox-item">
        <input type="checkbox" :id="'bt-' + bt.DefNum" :value="bt.DefNum" v-model="local.ignoredBlockouts" />
        <label :for="'bt-' + bt.DefNum">{{ bt.ItemName }}</label>
      </div>
    </div>

    <!-- Section: Patient-Facing Branding -->
    <div class="oa-form-group">
      <h3 class="oa-form-group-title">
        <i class="fa fa-paint-brush"></i>
        Patient-Facing Branding
      </h3>
      <p class="oa-form-help" style="margin-bottom: 12px;">
        Customize how your online booking page looks to patients. Set your office colors, logo, and a background photo.
      </p>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px;">
        <div>
          <label class="oa-form-label">Primary Color</label>
          <div style="display: flex; align-items: center; gap: 8px;">
            <input type="color" v-model="local.branding.primaryColor" style="width: 48px; height: 36px; border: 1px solid #ccc; border-radius: 4px; cursor: pointer; padding: 2px;" />
            <input type="text" class="oa-form-input" v-model="local.branding.primaryColor" style="flex: 1; font-family: monospace;" maxlength="7" />
          </div>
          <p class="oa-form-help">Used for buttons, accents, and highlights</p>
        </div>
        <div>
          <label class="oa-form-label">Secondary Color</label>
          <div style="display: flex; align-items: center; gap: 8px;">
            <input type="color" v-model="local.branding.secondaryColor" style="width: 48px; height: 36px; border: 1px solid #ccc; border-radius: 4px; cursor: pointer; padding: 2px;" />
            <input type="text" class="oa-form-input" v-model="local.branding.secondaryColor" style="flex: 1; font-family: monospace;" maxlength="7" />
          </div>
          <p class="oa-form-help">Used for "Returning Patient" button and confirmations</p>
        </div>
      </div>

      <div style="margin-bottom: 16px;">
        <label class="oa-form-label">Logo URL</label>
        <input type="text" class="oa-form-input" v-model="local.branding.logoUrl" placeholder="https://yoursite.com/logo.png" />
        <p class="oa-form-help">Direct link to your office logo image (PNG or JPG recommended)</p>
        <div v-if="local.branding.logoUrl" style="margin-top: 8px; padding: 12px; background: #f9fafb; border-radius: 8px; text-align: center;">
          <img :src="local.branding.logoUrl" alt="Logo preview" style="max-width: 160px; max-height: 80px; object-fit: contain;" @error="$event.target.style.display='none'" />
        </div>
      </div>

      <div>
        <label class="oa-form-label">Background Image URL</label>
        <input type="text" class="oa-form-input" v-model="local.branding.backgroundImageUrl" placeholder="https://yoursite.com/office-photo.jpg" />
        <p class="oa-form-help">A photo of your office (shown as background on the booking page)</p>
        <div v-if="local.branding.backgroundImageUrl" style="margin-top: 8px; padding: 12px; background: #f9fafb; border-radius: 8px; text-align: center;">
          <img :src="local.branding.backgroundImageUrl" alt="Background preview" style="max-width: 240px; max-height: 120px; object-fit: cover; border-radius: 4px;" @error="$event.target.style.display='none'" />
        </div>
      </div>
    </div>

    <!-- Save Button -->
    <div class="oa-save-row">
      <button class="oa-btn oa-btn-save" @click="save" :disabled="saving">
        <i class="fa fa-save"></i>
        {{ saving ? 'Saving...' : 'Save General Settings' }}
      </button>
    </div>

    <!-- Copy Settings Modal -->
    <CopySettingsModal
      ref="copyModal"
      :locations="paidLocations"
      :defaultToSiteId="selectedSiteId"
      @copied="onSettingsCopied"
    />

    <!-- UTM Link Builder Popover -->
    <UtmLinkBuilder
      :visible="showLinkBuilder"
      :shortUrl="linkBuilderShortUrl"
      :label="linkBuilderLabel"
      @close="showLinkBuilder = false"
      @copied="() => {}"
    />
  </section>
</template>

<script>
/* eslint-disable */
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import LocationSelector from './LocationSelector.vue';
import CopySettingsModal from './CopySettingsModal.vue';
import CommonOverlay from '@/components/CommonOverlay.vue';
import UtmLinkBuilder from './UtmLinkBuilder.vue';
import {
  AUTO_EXTEND_DEFAULT_MONTHS,
  INVALID_MINIMUM_NOTICE_MESSAGE,
  MINIMUM_NOTICE_MAX_DAYS,
  MINIMUM_NOTICE_MAX_HOURS,
  formatYmdLong,
  isYmd,
  localTodayYmd,
  minimumNoticeHelpText,
  normalizeAutoExtendMonths,
  normalizeMinimumNoticeHours,
  resolveEffectiveEnd,
  schedulingWindowNudge,
  splitMinimumNoticeHours,
  validateMinimumNoticeInput,
  validateSchedulingWindow
} from '@/utils/schedulingWindow';

export default {
  name: 'GeneralSettings',
  components: { Multiselect, LocationSelector, CopySettingsModal, CommonOverlay, UtmLinkBuilder },
  props: {
    settings: { type: Object, default: () => ({}) },
    siteName: { type: String, default: '' }, // Read-only practice name from sites table
    paidLocations: { type: Array, default: () => [] }, // Locations with ONLINE_SCHEDULING_PAID_FOR = TRUE
    locationStatus: { type: Object, default: () => ({}) }, // Status per location from store
    blockoutTypes: { type: Array, default: () => [] },
    confirmationStatuses: { type: Array, default: () => [] },
    procedureCodes: { type: Array, default: () => [] },
    providers: { type: Array, default: () => [] },
    currentSiteId: { type: String, default: '' }, // Currently selected siteId
    saving: { type: Boolean, default: false },
    // Server-computed booking window returned as a SIBLING of `settings`
    // ({ mode, start, end, months, todayYmd, empty, warnings, timezone }).
    // ABSENT on an older backend — every consumer must tolerate null.
    effectiveSchedulingWindow: { type: Object, default: null }
  },

  data() {
    return {
      selectedSiteId: '',
      // Dental insurance options
      insuranceOptions: [
        '1Dental',
        'Access Dental Plan',
        'Advantage Dental',
        'Aetna',
        'Aetna Better Health',
        'Aflac',
        'Aliera Healthcare',
        'Allegiance Benefit Plan Management',
        'AllSavers Insurance',
        'Alpha Dental Programs',
        'Always Care Benefits',
        'Ambetter',
        'AmeriPlan',
        'Ameritas',
        'Amerigroup',
        'AmeriHealth',
        'Anthem Blue Cross Blue Shield',
        'Arcadian Health Plan',
        'Assurant',
        'Athene',
        'Avesis',
        'Beam Dental',
        'Benecaid',
        'Best Life and Health Insurance',
        'Blue Cross Blue Shield',
        'Blue Cross Blue Shield of Alabama',
        'Blue Cross Blue Shield of Arizona',
        'Blue Cross Blue Shield of Florida',
        'Blue Cross Blue Shield of Georgia',
        'Blue Cross Blue Shield of Illinois',
        'Blue Cross Blue Shield of Kansas City',
        'Blue Cross Blue Shield of Louisiana',
        'Blue Cross Blue Shield of Massachusetts',
        'Blue Cross Blue Shield of Michigan',
        'Blue Cross Blue Shield of Minnesota',
        'Blue Cross Blue Shield of Mississippi',
        'Blue Cross Blue Shield of Montana',
        'Blue Cross Blue Shield of Nebraska',
        'Blue Cross Blue Shield of New Mexico',
        'Blue Cross Blue Shield of North Carolina',
        'Blue Cross Blue Shield of North Dakota',
        'Blue Cross Blue Shield of Oklahoma',
        'Blue Cross Blue Shield of Rhode Island',
        'Blue Cross Blue Shield of South Carolina',
        'Blue Cross Blue Shield of Tennessee',
        'Blue Cross Blue Shield of Texas',
        'Blue Cross Blue Shield of Vermont',
        'Blue Cross Blue Shield of Wyoming',
        'Blue Shield of California',
        'Buckeye Health Plan',
        'Capital Blue Cross',
        'Capitol Preferred',
        'Careington',
        'CareFirst BlueCross BlueShield',
        'CareOregon',
        'CareSource',
        'Capital District Physicians\' Health Plan (CDPHP)',
        'Centene Corporation',
        'Christian Healthcare Ministries',
        'Cigna',
        'Colorado Access',
        'Community First Health Plans',
        'CompBenefits',
        'Connecture DentalConnect',
        'Consolidated Health Plans',
        'Coventry Health Care',
        'DA Quest',
        'Dean Health Plan',
        'Delta Dental',
        'Delta Dental of California',
        'Delta Dental of Colorado',
        'Delta Dental of Illinois',
        'Delta Dental of Michigan',
        'Delta Dental of Minnesota',
        'Delta Dental of Missouri',
        'Delta Dental of New Jersey',
        'Delta Dental of New York',
        'Delta Dental of Ohio',
        'Delta Dental of Pennsylvania',
        'Delta Dental of Virginia',
        'Delta Dental of Washington',
        'Delta Dental of Wisconsin',
        'DentaQuest',
        'Dental Benefit Providers (DBP)',
        'Dental Health Alliance',
        'Dental Health Services',
        'Dental Network of America',
        'Dental Select',
        'DentCare Delivery Systems',
        'Dentegra Insurance',
        'DentiCare',
        'Dentrust Dental Plans',
        'Dominion Dental',
        'EmblemHealth',
        'Empire Blue Cross Blue Shield',
        'Encore Dental',
        'Excellus BlueCross BlueShield',
        'FEDVIP (Federal Employees Dental and Vision Insurance Program)',
        'Fidelis Care',
        'First Commonwealth',
        'First Health Network',
        'Forethought Life Insurance',
        'Friday Health Plans',
        'GEHA (Government Employees Health Association)',
        'Gerber Life Insurance',
        'Golden Rule Insurance',
        'Group Dental Service',
        'Guardian',
        'GuildNet',
        'HealthAmerica',
        'Health Alliance',
        'Healthcare USA',
        'HealthEase',
        'HealthFirst',
        'Health Net',
        'HealthNow New York',
        'HealthPartners',
        'Healthplex',
        'HealthSmart',
        'HealthSpring',
        'Highmark Blue Cross Blue Shield',
        'Hometown Health',
        'Horizon Blue Cross Blue Shield',
        'Humana',
        'Illinois Mutual',
        'Independence Blue Cross',
        'Kaiser Permanente',
        'Keystone Health Plan',
        'Liberty Dental Plan',
        'LifeWise Health Plan',
        'Lincoln Financial Group',
        'Magellan Healthcare',
        'Mango Health',
        'Managed Care of North America (MCNA)',
        'Marathon Health',
        'Martin\'s Point Health Care',
        'Maverest',
        'MedCost',
        'Medicaid',
        'Medical Mutual of Ohio',
        'Medi-Share',
        'Meritain Health',
        'MetLife',
        'Molina Healthcare',
        'MultiPlan',
        'Mutual of Omaha',
        'MVP Health Care',
        'Nationwide',
        'Neighborhood Health Plan',
        'Nippon Life Benefits',
        'Novasys Health',
        'Optima Health',
        'Oscar Health',
        'Oxford Health Plans',
        'Paramount Insurance',
        'Parkland Community Health Plan',
        'Peachstate Health Plan',
        'Peoples Health',
        'Physicians Health Plan',
        'Physicians Mutual',
        'Piedmont Community HealthCare',
        'PreferredOne',
        'Premera Blue Cross',
        'Principal Financial Group',
        'Priority Health',
        'Providence Health Plan',
        'QualCare',
        'Regence Blue Cross Blue Shield',
        'Renaissance Dental',
        'Rocky Mountain Health Plans',
        'SafeHealth Life Insurance Company',
        'Safety National',
        'Samaritan Health Plans',
        'SCAN Health Plan',
        'SelectHealth',
        'Sidecar Health',
        'Sierra Health and Life',
        'Simply Healthcare Plans',
        'Solstice Benefits',
        'Standard Insurance Company (The Standard)',
        'Starmount Life Insurance',
        'SummaCare',
        'Sun Life Financial',
        'Sunshine Health',
        'Superior Dental Care',
        'Tufts Health Plan',
        'TRICARE',
        'Trustmark',
        'UMR (United Medical Resources)',
        'UniCare',
        'Unimerica Insurance',
        'Unum',
        'United Concordia',
        'UnitedHealthcare',
        'UnitedHealthOne',
        'Universal Health Fellowship',
        'UPMC Health Plan',
        'VA (Veterans Affairs)',
        'WellCare',
        'Wellmark Blue Cross Blue Shield',
        'WellPoint',
        'Western Health Advantage',
        'Willamette Dental',
        'Other',
        'No Insurance / Self-Pay'
      ],


      // Per-location short URLs: { siteId: shortUrl }
      locationShortUrls: {},
      locationUrlStatus: {},
      urlCopiedId: null,
      generatingShortUrls: false,
      // UTM Link Builder
      showLinkBuilder: false,
      linkBuilderShortUrl: '',
      linkBuilderLabel: '',

      // Single-link short URL (for 'combined' and 'default' modes)
      singleLinkShortUrl: '',
      singleLinkStatus: '', // 'loading' | 'success' | 'error'

      // Wiki templates available for the post-booking confirmation SMS
      // (filtered to pages tagged 'online-appointments')
      confirmationTemplates: [],
      loadingConfirmationTemplates: false,

      // Minimum notice inputs (number box + Hours/Days). Kept OUT of `local` on purpose:
      // save() spreads `local` into the payload, and only the converted integer
      // `minimumNoticeHours` may be stored — never `value`/`unit`.
      noticeInput: { value: '', unit: 'hours' },
      minimumNoticeMaxHours: MINIMUM_NOTICE_MAX_HOURS,
      minimumNoticeMaxDays: MINIMUM_NOTICE_MAX_DAYS,
      invalidMinimumNoticeMessage: INVALID_MINIMUM_NOTICE_MESSAGE,

      local: {
        // Online Scheduling Status
        onlineSchedulingEnabled: true,
        schedulingWindowStart: null,
        schedulingWindowEnd: null,
        // Absent in stored settings => OFF (today's behaviour for every existing site)
        schedulingWindowAutoExtend: false,
        schedulingWindowAutoExtendMonths: AUTO_EXTEND_DEFAULT_MONTHS,

        // Practice Info (practiceName removed - now read-only from siteName prop)
        notificationEmail: '',
        sendAppointmentNotifications: false,

        // Multi-Location
        locationMode: 'combined', // 'combined' | 'default' | 'separate'
        allowPatientLocationChoice: true,
        enabledLocations: [],
        defaultLocationId: null,

        // Insurance
        acceptedInsurances: [],
        providerNetworkStatus: {},
        showNetworkStatusChart: false,
        showNetworkStatusOnProviderSelect: false,

        // Office Phone
        officePhone: '',

        // Post-Booking Redirect URL
        thankYouUrl: '',

        // No Fee Code (object for vue-multiselect, string ProcCode extracted on save)
        noFeeCodeObj: null,

        // Appointment Confirmation Status
        appointmentConfirmedStatus: null,

        // Wiki page ID for post-booking SMS confirmation (null = use hardcoded default)
        confirmationWikiPageID: null,

        // Blockouts
        ignoredBlockouts: [],

        // Patient-Facing Branding
        branding: {
          primaryColor: '#2563EB',
          secondaryColor: '#16A34A',
          logoUrl: '',
          backgroundImageUrl: ''
        }
      }
    };
  },

  computed: {
    hasMultipleLocations() {
      // Show location selection only if there are more than 1 paid locations
      return this.paidLocations && this.paidLocations.length > 1;
    },
    providersList() {
      return this.providers.map(p => {
        const name = `${p.FName || ''} ${p.LName || ''}`.trim();
        return p.IsSecondary === 0 ? `Dr. ${name}` : name;
      });
    },

    // "Today" in the OFFICE's timezone when the backend tells us (it computes the window
    // there), otherwise the browser's day. Used so the rolling-End hint is right for an
    // admin working from a different timezone than the practice.
    schedulingWindowTodayYmd() {
      const fromServer = this.effectiveSchedulingWindow && this.effectiveSchedulingWindow.todayYmd;
      return isYmd(fromServer) ? fromServer : localTodayYmd();
    },

    // Computed CLIENT-SIDE (not read off the server sibling) so it updates as soon as the
    // dropdown changes and is never stale right after a save.
    rollingEndYmd() {
      if (this.local.schedulingWindowAutoExtend !== true) return null;
      return resolveEffectiveEnd({
        autoExtend: true,
        months: this.local.schedulingWindowAutoExtendMonths,
        fixedEnd: null,
        todayYmd: this.schedulingWindowTodayYmd
      });
    },

    effectiveEndLabel() {
      return formatYmdLong(this.rollingEndYmd);
    },

    // Warn, don't block: a Start Date past the rolling End self-heals as the window rolls.
    schedulingWindowStartWarning() {
      if (!this.rollingEndYmd) return false;
      const start = this.local.schedulingWindowStart;
      return !!start && start > this.rollingEndYmd;
    },

    schedulingWindowNudgeText() {
      if (!this.local.onlineSchedulingEnabled) return null;
      return schedulingWindowNudge(this.local, this.schedulingWindowTodayYmd);
    },

    // Live help line for Minimum Notice; null while the typed value is invalid (the
    // template shows the warning instead, and save() refuses it).
    minimumNoticeHelp() {
      return minimumNoticeHelpText(this.noticeInput.value, this.noticeInput.unit);
    }
  },

  watch: {
    // Sync selectedSiteId from prop and load site-scoped wiki templates
    currentSiteId: {
      handler(newVal) {
        if (newVal && newVal !== this.selectedSiteId) {
          this.selectedSiteId = newVal;
        }
        if (newVal) {
          this.loadConfirmationTemplates();
        }
      },
      immediate: true
    },

    // Emit location change when user selects a different location
    selectedSiteId(newVal, oldVal) {
      if (newVal && newVal !== oldVal && oldVal !== '') {
        this.$emit('location-changed', newVal);
      }
    },

    settings: {
      handler(newVal) {
        if (newVal) {
          this.syncFromSettings(newVal);
        }
      },
      immediate: true,
      deep: true
    },

    // First time the box is checked, land on a sane month count instead of whatever
    // (possibly invalid) value was stored. A rolling window with no month count would
    // be a config that can never fire — the save handler refuses it outright.
    'local.schedulingWindowAutoExtend'(newVal) {
      if (newVal === true && normalizeAutoExtendMonths(this.local.schedulingWindowAutoExtendMonths) === null) {
        this.local.schedulingWindowAutoExtendMonths = AUTO_EXTEND_DEFAULT_MONTHS;
      }
    },

    // When locationMode changes, generate the appropriate short URL(s)
    'local.locationMode'(newVal) {
      if (newVal === 'separate') {
        this.generateLocationShortUrls();
      } else if (newVal === 'combined' || newVal === 'default') {
        this.generateSingleLinkShortUrl();
      }
    },

    // When default location changes, regenerate the single-link short URL
    'local.defaultLocationId'(newVal) {
      if (this.local.locationMode === 'default' && newVal) {
        this.generateSingleLinkShortUrl();
      }
    },

    // When insurances are added, initialize their provider network status with all providers in-network
    'local.acceptedInsurances': {
      handler(newInsurances, oldInsurances) {
        if (!newInsurances) return;

        newInsurances.forEach(insurance => {
          if (!this.local.providerNetworkStatus[insurance]) {
            // Initialize with all providers in-network (checked)
            this.$set(this.local.providerNetworkStatus, insurance, {});
            this.providersList.forEach(provider => {
              this.$set(this.local.providerNetworkStatus[insurance], provider, true);
            });
          }
        });

        // Clean up removed insurances
        if (oldInsurances) {
          Object.keys(this.local.providerNetworkStatus).forEach(insurance => {
            if (!newInsurances.includes(insurance)) {
              this.$delete(this.local.providerNetworkStatus, insurance);
            }
          });
        }
      },
      deep: true
    }
  },

  methods: {
    async loadConfirmationTemplates() {
      try {
        this.loadingConfirmationTemplates = true;
        const result = await this.$store.dispatch('wiki/getWikiPages', {
          siteID: this.currentSiteId,
          tags: 'online-appointments'
        });
        // getWikiPages returns [wikiPages, siteTags, allTags, forms] from a multi-statement query
        if (result && Array.isArray(result) && result.length > 0 && Array.isArray(result[0])) {
          this.confirmationTemplates = result[0];
        } else {
          this.confirmationTemplates = [];
        }
      } catch (err) {
        console.error('Error loading online-appointments wiki templates:', err);
        this.confirmationTemplates = [];
      } finally {
        this.loadingConfirmationTemplates = false;
      }
    },

    syncFromSettings(s) {
      // Online Scheduling Status
      this.local.onlineSchedulingEnabled = s.onlineSchedulingEnabled !== false; // default true for backwards compat
      this.local.schedulingWindowStart = s.schedulingWindowStart || null;
      this.local.schedulingWindowEnd = s.schedulingWindowEnd || null;
      // Absent / anything but literal true => OFF. The stored End Date above is kept
      // untouched while rolling, so unchecking the box restores it.
      this.local.schedulingWindowAutoExtend = s.schedulingWindowAutoExtend === true;
      this.local.schedulingWindowAutoExtendMonths =
        normalizeAutoExtendMonths(s.schedulingWindowAutoExtendMonths) || AUTO_EXTEND_DEFAULT_MONTHS;
      // Minimum notice: absent / invalid => blank (no minimum). 48 comes back as "2 Days".
      this.noticeInput = splitMinimumNoticeHours(normalizeMinimumNoticeHours(s.minimumNoticeHours) || 0);

      // Practice Info (practiceName is now read-only from siteName prop)
      this.local.notificationEmail = s.notificationEmail || '';
      this.local.sendAppointmentNotifications = s.sendAppointmentNotifications || false;

      // Wiki page ID for post-booking SMS confirmation
      this.local.confirmationWikiPageID = s.confirmationWikiPageID || null;

      // Multi-Location — locationMode with backward compat
      if (s.locationMode) {
        this.local.locationMode = s.locationMode;
      } else if (s.allowPatientLocationChoice === true) {
        this.local.locationMode = 'combined';
      } else {
        this.local.locationMode = 'default';
      }
      this.local.allowPatientLocationChoice = (this.local.locationMode === 'combined');
      // Migrate old string format to object format for backward compatibility
      this.local.enabledLocations = (s.enabledLocations || []).map(el => {
        if (typeof el === 'string') {
          const match = this.paidLocations.find(pl => (pl.id || pl.siteId) === el);
          return { siteId: el, name: match ? match.name : '', city: match ? match.city : '', state: match ? match.state : '', logo: '' };
        }
        return { ...el };
      });
      this.local.defaultLocationId = s.defaultLocationId || null;

      // Insurance
      this.local.acceptedInsurances = s.acceptedInsurances ? [...s.acceptedInsurances] : [];
      this.local.providerNetworkStatus = s.providerNetworkStatus ? JSON.parse(JSON.stringify(s.providerNetworkStatus)) : {};
      this.local.showNetworkStatusChart = s.showNetworkStatusChart || false;
      this.local.showNetworkStatusOnProviderSelect = s.showNetworkStatusOnProviderSelect || false;

      // Office Phone
      this.local.officePhone = s.officePhone || '';

      // Post-Booking Redirect URL
      this.local.thankYouUrl = s.thankYouUrl || '';

      // No Fee Code (resolve string ProcCode to full object for multiselect)
      if (s.noFeeCode) {
        this.local.noFeeCodeObj = this.procedureCodes.find(pc => pc.ProcCode === s.noFeeCode) || null;
      } else {
        this.local.noFeeCodeObj = null;
      }

      // Appointment Confirmation Status
      this.local.appointmentConfirmedStatus = s.appointmentConfirmedStatus || null;

      // Blockouts
      this.local.ignoredBlockouts = s.ignoredBlockouts ? [...s.ignoredBlockouts] : [];

      // Branding
      this.local.branding = {
        primaryColor: (s.branding && s.branding.primaryColor) || '#2563EB',
        secondaryColor: (s.branding && s.branding.secondaryColor) || '#16A34A',
        logoUrl: (s.branding && s.branding.logoUrl) || '',
        backgroundImageUrl: (s.branding && s.branding.backgroundImageUrl) || ''
      };

      // Generate short URLs on load based on current mode
      if (!this.hasMultipleLocations) {
        // Single-office: always generate the booking link
        this.generateSingleLinkShortUrl();
      } else if (this.local.locationMode === 'separate') {
        this.generateLocationShortUrls();
      } else if (this.local.locationMode === 'combined' || (this.local.locationMode === 'default' && this.local.defaultLocationId)) {
        this.generateSingleLinkShortUrl();
      }
    },

    isProviderInNetwork(insurance, provider) {
      return this.local.providerNetworkStatus[insurance]?.[provider] === true;
    },

    isAllProvidersChecked(insurance) {
      const status = this.local.providerNetworkStatus[insurance];
      if (!status) return false;
      return this.providersList.every(provider => status[provider] === true);
    },

    isIndeterminate(insurance) {
      const status = this.local.providerNetworkStatus[insurance];
      if (!status) return false;
      const checkedCount = this.providersList.filter(provider => status[provider] === true).length;
      return checkedCount > 0 && checkedCount < this.providersList.length;
    },

    toggleProvider(insurance, provider, checked) {
      if (!this.local.providerNetworkStatus[insurance]) {
        this.$set(this.local.providerNetworkStatus, insurance, {});
      }
      this.$set(this.local.providerNetworkStatus[insurance], provider, checked);
    },

    toggleAllProviders(insurance, checked) {
      if (!this.local.providerNetworkStatus[insurance]) {
        this.$set(this.local.providerNetworkStatus, insurance, {});
      }
      this.providersList.forEach(provider => {
        this.$set(this.local.providerNetworkStatus[insurance], provider, checked);
      });
    },

    isLocationEnabled(siteId) {
      return this.local.enabledLocations.some(el =>
        (typeof el === 'string' ? el : el.siteId) === siteId
      );
    },

    toggleLocation(loc) {
      const siteId = loc.id || loc.siteId;
      const idx = this.local.enabledLocations.findIndex(el =>
        (typeof el === 'string' ? el : el.siteId) === siteId
      );
      if (idx >= 0) {
        this.local.enabledLocations.splice(idx, 1);
      } else {
        this.local.enabledLocations.push({
          siteId: siteId,
          name: loc.name || '',
          city: loc.city || '',
          state: loc.state || '',
          logo: loc.logo || ''
        });
      }
    },

    async generateLocationShortUrls() {
      if (this.generatingShortUrls || !this.paidLocations.length) return;
      this.generatingShortUrls = true;
      try {
        for (const loc of this.paidLocations) {
          const siteId = loc.id || loc.siteId;
          // Skip if we already have a short URL for this location
          if (this.locationShortUrls[siteId]) continue;
          await this.fetchShortUrlForLocation(siteId);
        }
      } finally {
        this.generatingShortUrls = false;
      }
    },

    async fetchShortUrlForLocation(siteId) {
      this.$set(this.locationUrlStatus, siteId, 'loading');
      const bookingUrl = `${window.location.origin}/online-scheduling/book-appointment.html?siteID=${siteId}`;

      // Timeout: mark as error after 10 seconds
      const timeoutId = setTimeout(() => {
        if (this.locationUrlStatus[siteId] === 'loading') {
          this.$set(this.locationUrlStatus, siteId, 'error');
        }
      }, 10000);

      try {
        const response = await axios.post('/get-booking-short-url', {
          siteID: siteId,
          bookingUrl: bookingUrl
        }, { crossdomain: true });
        clearTimeout(timeoutId);
        const shortUrl = typeof response.data === 'string' ? response.data : (response.data && response.data.data);
        if (shortUrl) {
          this.$set(this.locationShortUrls, siteId, shortUrl);
        } else {
          this.$set(this.locationShortUrls, siteId, bookingUrl);
        }
        this.$set(this.locationUrlStatus, siteId, 'success');
      } catch (err) {
        clearTimeout(timeoutId);
        console.error('Error generating short URL for siteId:', siteId, err);
        this.$set(this.locationUrlStatus, siteId, 'error');
      }
    },

    async retryLocationUrl(siteId) {
      // Clear previous state and retry
      this.$delete(this.locationShortUrls, siteId);
      await this.fetchShortUrlForLocation(siteId);
    },

    async refreshAllLocationUrls() {
      // Clear all URLs and statuses, then regenerate
      this.locationShortUrls = {};
      this.locationUrlStatus = {};
      await this.generateLocationShortUrls();
    },

    getDefaultLocationName() {
      if (!this.local.defaultLocationId) return '';
      const loc = this.paidLocations.find(l => (l.id || l.siteId) === this.local.defaultLocationId);
      if (!loc) return '';
      let name = loc.name;
      if (loc.city) name += `, ${loc.city}`;
      return name;
    },

    async generateSingleLinkShortUrl() {
      // Determine which siteID to use for the booking URL
      let siteId;
      if (!this.hasMultipleLocations) {
        // Single-office: use the current site
        siteId = this.currentSiteId || this.selectedSiteId;
      } else if (this.local.locationMode === 'default') {
        siteId = this.local.defaultLocationId;
      } else {
        // 'combined' mode — use the first paid location as the entry point
        siteId = this.paidLocations.length > 0 ? (this.paidLocations[0].id || this.paidLocations[0].siteId) : null;
      }
      if (!siteId) return;

      this.singleLinkStatus = 'loading';
      this.singleLinkShortUrl = '';
      const bookingUrl = `${window.location.origin}/online-scheduling/book-appointment.html?siteID=${siteId}`;

      const timeoutId = setTimeout(() => {
        if (this.singleLinkStatus === 'loading') {
          this.singleLinkStatus = 'error';
        }
      }, 10000);

      try {
        const response = await axios.post('/get-booking-short-url', {
          siteID: siteId,
          bookingUrl: bookingUrl
        }, { crossdomain: true });
        clearTimeout(timeoutId);
        const shortUrl = typeof response.data === 'string' ? response.data : (response.data && response.data.data);
        this.singleLinkShortUrl = shortUrl || bookingUrl;
        this.singleLinkStatus = 'success';
      } catch (err) {
        clearTimeout(timeoutId);
        console.error('Error generating single-link short URL:', err);
        this.singleLinkStatus = 'error';
      }
    },

    copySingleLinkUrl() {
      if (!this.singleLinkShortUrl) return;
      this.linkBuilderShortUrl = this.singleLinkShortUrl;
      this.linkBuilderLabel = 'Your Booking Link';
      this.showLinkBuilder = true;
    },

    copyLocationUrl(siteId) {
      const url = this.locationShortUrls[siteId];
      if (!url) return;
      const loc = this.paidLocations.find(l => (l.id || l.siteId) === siteId);
      this.linkBuilderShortUrl = url;
      this.linkBuilderLabel = loc ? loc.name : 'Booking Link';
      this.showLinkBuilder = true;
    },

    noFeeCodeLabel(code) {
      if (!code) return '';
      return `${code.ProcCode} — [${code.Descript || ''}]`;
    },

    save() {
      // Validate scheduling window (refuses a rolling window with no usable month count;
      // skips the start<=end rule while rolling, where the stored End Date is ignored)
      const windowCheck = validateSchedulingWindow(this.local);
      if (!windowCheck.ok) {
        this.$toast.error(windowCheck.message);
        return;
      }

      // Minimum notice: refuse anything the server would refuse, before the round trip.
      const noticeCheck = validateMinimumNoticeInput(this.noticeInput.value, this.noticeInput.unit);
      if (!noticeCheck.ok) {
        this.$toast.error(noticeCheck.message);
        return;
      }

      // Validate required fields
      if (!this.local.noFeeCodeObj) {
        this.$toast.error('Please select a No Fee Code before saving.');
        return;
      }
      if (!this.local.appointmentConfirmedStatus) {
        this.$toast.error('Please select an Appointment Confirmation Status before saving.');
        return;
      }

      if (this.local.thankYouUrl) {
        let url = this.local.thankYouUrl.trim();
        if (!/^https?:\/\//i.test(url)) {
          url = 'https://' + url;
        }
        try {
          new URL(url);
        } catch (e) {
          this.$toast.error('Post-Booking Redirect URL is not a valid URL. Example: https://yourwebsite.com/thank-you');
          return;
        }
        this.local.thankYouUrl = url;
      }

      // Extract string ProcCode from multiselect object for storage
      const payload = { ...this.local };
      payload.minimumNoticeHours = noticeCheck.hours; // one integer, days already × 24
      payload.noFeeCode = this.local.noFeeCodeObj ? this.local.noFeeCodeObj.ProcCode : null;
      delete payload.noFeeCodeObj;
      // Write both locationMode and allowPatientLocationChoice for backward compat
      payload.locationMode = this.local.locationMode;
      payload.allowPatientLocationChoice = (this.local.locationMode === 'combined');
      this.$emit('save', payload);
    },

    showCopyModal() {
      this.$refs.copyModal.show();
    },

    onSettingsCopied({ toSiteId }) {
      // If settings were copied to the currently selected location, reload
      if (toSiteId === this.selectedSiteId) {
        this.$emit('location-changed', this.selectedSiteId);
      }
    }
  }
};
</script>
